import { can, canAny, canAll } from '@massar/shared/permissions';

/**
 * UI gating only — enforcement layer 5 in docs/03 §4.
 *
 * Hiding a button is a convenience for the user, never a security control: the API
 * re-checks every permission and every section scope. Nothing here may be the only
 * thing standing between a caller and an action.
 */
export { can, canAny, canAll };

/**
 * "Does this user hold the permission **at group level**?"
 *
 * Not the same question as `can(access, p, null)`. The shared helper reads a null
 * section as *"in any section I hold"*, which is right for "should this menu appear at
 * all" but wrong for a group-wide screen: a user granted `report.consolidated.view` in
 * Agency alone would pass it and reach the consolidated total for every section
 * (docs/03 §2 — that permission is the key to the whole group's cash position).
 *
 * Global scope means the grant came from an assignment with `sectionId === null`, which
 * is exactly what `access.global` holds.
 */
export const canGlobal = (access, permission) => {
  if (!access) return false;
  if (access.isSuperAdmin) return true;
  return !!access.global?.includes(permission);
};

export const canGlobalAny = (access, permissions = []) =>
  permissions.some((permission) => canGlobal(access, permission));

/**
 * Resolve the section a permission check should be evaluated against.
 *
 * `null` means "any section the user holds", which is what a global/group view wants.
 * A section-scoped screen passes the active section so a user who holds
 * `invoice.approve` in Agency does not see an approve button while looking at
 * Real Estate.
 */
export const scopeFor = (activeSectionId) => activeSectionId || null;

/** True when the user can reach the group-wide numbers (docs/03 §2). */
export const isConsolidatedViewer = (access) => can(access, 'report.consolidated.view');
